import { Injectable, inject } from '@angular/core';
import { Observable, of, map, switchMap, catchError } from 'rxjs';
import {
  PaginatedDropdownLoadParams,
  PaginatedOptionsPage,
} from '@shared/utils/paginated-dropdown.util';

import {
  OfferCreateRequest,
  OfferResponse,
  OfferStatusUpdateRequest,
  OfferUpdateRequest,
  OffersService,
} from '@app/api/promotion-service';
import type { PageInfoOfferResponse } from '@app/api/promotion-service/model/pageInfoOfferResponse';
import {
  OfferListItem,
  OfferSearchParams,
  OfferUiStatus,
} from '../models/offer.interface';
import { formatApiLocalTime } from '../components/offer-upsert/offer-date-time.util';

@Injectable({
  providedIn: 'root',
})
export class OfferService {
  private readonly offersService = inject(OffersService);

  getOffersOptionsPage(
    params: PaginatedDropdownLoadParams,
  ): Observable<PaginatedOptionsPage<number>> {
    const searchText = params.searchText?.trim() || undefined;
    return this.getOffers({
      page: params.page - 1,
      size: params.size,
      code: searchText,
      displayName: searchText,
      sortBy: 'lastUpdateDate',
      sortOrder: 'desc',
    })
      .pipe(
        map((res) => {
          return {
            options: res.items.map((x) => ({
              label: `${x.code ?? ''} - ${x.displayName ?? ''}`,
              value: Number(x.id),
            })),
            pageContentLength: res.items.length,
            totalElements: res.totalCount,
          };
        }),
        catchError(() => of({ options: [], pageContentLength: 0 })),
      );
  }

  /**
   * Fetch, filter, sort and paginate Offers in memory.
   * Backend offers endpoint does not support advanced query params.
   */
  getOffers(params: OfferSearchParams): Observable<{
    items: OfferListItem[];
    totalCount: number;
  }> {
    const {
      page = 0,
      size = 20,
      sortBy,
      sortOrder = 'desc',
      code,
      displayName,
      status,
      kind,
    } = params;

    const hasClientFilters = !!(code || displayName || status || kind);
    const pageRequest = {
      page: hasClientFilters ? 0 : Math.max(page, 0),
      size: hasClientFilters ? 1000 : Math.max(size, 1),
      sortBy,
      ascending: sortOrder === 'asc',
    };

    return this.offersService
      .list(pageRequest, 'body', false, {
        httpHeaderAccept: 'application/json' as any,
      })
      .pipe(
        map((res: PageInfoOfferResponse) => {
          const source = res?.pageContent ?? [];
          if (!hasClientFilters) {
            return {
              items: source.map((x) => this.mapOfferResponseToItem(x)),
              totalCount: res?.totalElements ?? source.length,
            };
          }

          const filtered = this.filterOffers(source, {
            code,
            displayName,
            status,
            kind,
          });
          const sorted = this.sortOffers(filtered, sortBy, sortOrder);
          const start = Math.max(page, 0) * Math.max(size, 1);
          const end = start + Math.max(size, 1);
          return {
            items: sorted.slice(start, end).map((x) => this.mapOfferResponseToItem(x)),
            totalCount: sorted.length,
          };
        }),
      );
  }

  getOfferById(id: string | number): Observable<OfferResponse> {
    const offerId = typeof id === 'string' ? Number(id) : id;
    return this.offersService.get(offerId, 'body', false, {
      httpHeaderAccept: 'application/json' as any,
    });
  }

  createOffer(offer: OfferCreateRequest): Observable<OfferResponse> {
    return this.offersService.create(offer, 'body', false, {
      httpHeaderAccept: 'application/json' as any,
    });
  }

  updateOffer(
    id: string | number,
    offer: OfferUpdateRequest,
  ): Observable<OfferResponse> {
    const offerId = typeof id === 'string' ? Number(id) : id;
    return this.offersService.update(offerId, offer, 'body', false, {
      httpHeaderAccept: 'application/json' as any,
    });
  }

  inactivateOffer(id: string | number): Observable<any> {
    const offerId = typeof id === 'string' ? Number(id) : id;
    const payload: OfferStatusUpdateRequest = {
      status: OfferStatusUpdateRequest.StatusEnum.Inactive,
    };
    return this.offersService.updateStatus(offerId, payload, 'body', false, {
      httpHeaderAccept: 'application/json' as any,
    });
  }

  deleteOffer(id: string | number): Observable<any> {
    return this.inactivateOffer(id);
  }

  toggleOfferStatus(
    id: string | number,
    nextUiStatus: OfferUiStatus,
  ): Observable<OfferResponse> {
    const nextApiStatus = this.mapUiStatusToApiStatus(nextUiStatus);
    const offerId = typeof id === 'string' ? Number(id) : id;

    return this.offersService
      .get(offerId, 'body', false, {
        httpHeaderAccept: 'application/json' as any,
      })
      .pipe(
      switchMap((current: OfferResponse) => {
        const updated = this.mapOfferResponseToUpdate(current, nextApiStatus);
        return this.offersService.update(offerId, updated, 'body', false, {
          httpHeaderAccept: 'application/json' as any,
        });
      }),
    );
  }

  private mapOfferResponseToUpdate(
    current: OfferResponse,
    status: string,
  ): OfferUpdateRequest {
    return {
      code: current.code,
      displayName: current.displayName,
      shortDescription: current.shortDescription,
      priority: current.priority,
      startDate: current.startDate,
      endDate: current.endDate,
      startTime: formatApiLocalTime(current.startTime) as OfferUpdateRequest['startTime'],
      endTime: formatApiLocalTime(current.endTime) as OfferUpdateRequest['endTime'],
      definitionJson: current.definitionJson,
      salesChannelCodes: current.salesChannelCodes,
      kind: current.kind as OfferUpdateRequest.KindEnum | undefined,
      status: status as OfferUpdateRequest.StatusEnum,
    };
  }

  private filterOffers(
    items: OfferResponse[],
    filters: {
      code?: string;
      displayName?: string;
      status?: OfferUiStatus;
      kind?: string;
    },
  ): OfferResponse[] {
    const normalizedCode = (filters.code ?? '').toString().trim().toLowerCase();
    const normalizedDisplayName = (filters.displayName ?? '')
      .toString()
      .trim()
      .toLowerCase();

    return items.filter((x) => {
      const codeOk =
        !normalizedCode ||
        (x.code ?? '').toString().toLowerCase().includes(normalizedCode);

      const displayNameOk =
        !normalizedDisplayName ||
        (x.displayName ?? '')
          .toString()
          .toLowerCase()
          .includes(normalizedDisplayName);

      const statusOk =
        !filters.status ||
        this.mapApiStatusToUiStatus(x.status) === filters.status;

      const kindOk = !filters.kind || (x.kind ?? '') === filters.kind;

      return codeOk && displayNameOk && statusOk && kindOk;
    });
  }

  private sortOffers(
    items: OfferResponse[],
    sortBy?: string,
    sortOrder: 'asc' | 'desc' = 'desc',
  ): OfferResponse[] {
    if (!sortBy) return [...items];

    const order = sortOrder === 'asc' ? 1 : -1;
    const list = [...items];

    list.sort((a, b) => {
      const av = (a as any)[sortBy];
      const bv = (b as any)[sortBy];

      if (av === bv) return 0;
      if (av === undefined || av === null) return 1;
      if (bv === undefined || bv === null) return -1;

      // Dates are strings (ISO). Numeric fields are numbers.
      if (sortBy === 'createdAt' || sortBy === 'updatedAt') {
        const at = new Date(av as any).getTime();
        const bt = new Date(bv as any).getTime();
        return (at - bt) * order;
      }

      if (typeof av === 'number' && typeof bv === 'number') {
        return (av - bv) * order;
      }

      return String(av).localeCompare(String(bv)) * order;
    });

    return list;
  }

  private mapOfferResponseToItem(x: OfferResponse): OfferListItem {
    return {
      id: (x.id ?? '').toString(),
      code: x.code ?? '',
      displayName: x.displayName ?? '',
      kind: x.kind,
      startDate: x.startDate,
      endDate: x.endDate,
      startTime: x.startTime,
      endTime: x.endTime,
      priority: x.priority ?? undefined,
      salesChannelCodes: x.salesChannelCodes ?? undefined,
      status: this.mapApiStatusToUiStatus(x.status),
      updatedAt: x.updatedAt ?? undefined,
    };
  }

  private mapApiStatusToUiStatus(apiStatus?: string): OfferUiStatus {
    switch (apiStatus) {
      case 'ACTIVE':
      case 'ENABLED':
        return 'active';
      case 'ENDED':
      case 'INACTIVE':
      case 'DISABLED':
        return 'inactive';
      case 'DRAFT':
      default:
        return 'draft';
    }
  }

  private mapUiStatusToApiStatus(
    uiStatus: OfferUiStatus,
  ): string {
    switch (uiStatus) {
      case 'active':
        return 'ACTIVE';
      case 'inactive':
        return 'INACTIVE';
      case 'draft':
      default:
        return 'DRAFT';
    }
  }
}

